/**
 * Four-tab connection catalog for AgentForge: HF · Cloudflare · CivitAI · Forge endpoints.
 */
import { useEffect, useRef } from "react";
import {
  fetchConnectionCreds,
  mountGpuTabs,
  PRIMARY_TOOL,
  type Connection,
  type ConnectionKind,
  type GpuMountHandle,
  type GpuPluginHost,
} from "@forgesuite/gpu-browser";
import "@forgesuite/gpu-browser/styles.css";
import { getAccessToken } from "../suite/auth";
import { loadSuiteSettings } from "../suite/settings";
import type { WalletSnapshot } from "../suite/types";
import { SUITE_MEMBER_PRICE_USD } from "../suite/access";
import { isCatalogSource, type InferenceSource } from "../suite/inference";
import type { AgentBrain } from "../lib/endpoint";
import {
  normalizeEndpointUrl,
  saveBrainMode,
  saveEndpoint,
} from "../lib/endpoint";
import { useCatalogRuntime } from "../lib/useCatalogRuntime";

type Props = {
  wallet: WalletSnapshot | null;
  onStatus: (msg: string) => void;
  onBrainChange: (b: AgentBrain | null) => void;
  /** AccessHub tool_id — dump ∩ this tool's model_type. */
  tool?: string;
};

function kindToSource(kind: ConnectionKind | string): InferenceSource {
  return isCatalogSource(kind) ? kind : "custom";
}

export function GpuBrowserMount({
  wallet,
  onStatus,
  onBrainChange,
  tool = PRIMARY_TOOL.agentforge,
}: Props) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<GpuMountHandle | null>(null);
  const busyRef = useRef(false);
  const { selectorOn } = useCatalogRuntime("agentforge");

  useEffect(() => {
    if (selectorOn !== true) return;
    const el = elRef.current;
    if (!el) return;

    const forgeApiUrl = loadSuiteSettings().forgeApiUrl;

    const applyBrain = (b: AgentBrain | null) => {
      saveEndpoint(b);
      onBrainChange(b);
    };

    const connectFromRow = async (conn: Connection) => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        let endpointUrl = conn.endpointUrl || "";
        let apiKey = getAccessToken() || "";
        let model = conn.modelId || undefined;

        if (!endpointUrl || conn.kind === "forge_endpoint") {
          try {
            const creds = await fetchConnectionCreds({ forgeApiUrl, getAccessToken }, conn.id);
            if (creds.endpointUrl) endpointUrl = creds.endpointUrl;
            if (creds.modelId) model = creds.modelId;
          } catch {
            /* catalog rows often have no dedicated URL — fall back to suite relay */
          }
        }

        if (!endpointUrl) {
          endpointUrl = `${forgeApiUrl.replace(/\/$/, "")}/v1`;
        }

        const source = kindToSource(conn.kind);
        const ep: AgentBrain = {
          bindKind: /vision|vl|multimodal|image/i.test(`${conn.modelId} ${conn.modality}`)
            ? "openai-vision"
            : "openai",
          endpointUrl: normalizeEndpointUrl(endpointUrl),
          model,
          apiKey,
          label: `${conn.label}`,
          source,
        };
        applyBrain(ep);
        saveBrainMode(source);
        onStatus(`Brain · ${ep.label}`);
      } finally {
        busyRef.current = false;
      }
    };

    const host: GpuPluginHost = {
      productName: "AgentForge",
      appId: "agentforge",
      purpose: "vision",
      accessHubTool: tool || PRIMARY_TOOL.agentforge,
      dumpCatalogUrl: "https://proxy.vividlex.com",
      forgeApiUrl,
      getAccessToken,
      wallet: {
        balanceUsd: wallet?.balanceUsd ?? 0,
        isMember: Boolean(wallet?.subscriptionActive),
        memberPriceUsd: SUITE_MEMBER_PRICE_USD,
        subscriptionLabel: wallet?.subscriptionLabel ?? null,
      },
      favoritesStorageKey: "agentforge.modelFavorites.v1",
      connectCtaLabel: "Connect",
      connectModalities: "all",
      defaultInferenceModality: "text-generation",
      connecting: busyRef.current,
      onToast: (_level, message) => onStatus(message),
      onAccessHubModel: (modelId, meta) => {
        const now = new Date().toISOString();
        void connectFromRow({
          id: meta.dumpId || modelId,
          kind: "hf",
          sourceTable: "dump",
          sourceId: modelId,
          appOrigin: null,
          label: meta.name || modelId,
          modelId,
          engine: null,
          modality: "text-generation",
          endpointUrl: `${forgeApiUrl.replace(/\/$/, "")}/v1`,
          status: "ready",
          ready: true,
          visibility: "public",
          lastHealthAt: null,
          lastUsedAt: null,
          createdAt: now,
          updatedAt: now,
        });
      },
      async onConnect(conn) {
        await connectFromRow(conn);
      },
    };

    handleRef.current?.destroy();
    handleRef.current = mountGpuTabs(el, {
      host,
      defaultTab: "cloudflare",
      showTabBar: true,
    });

    return () => {
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, [
    selectorOn,
    wallet?.balanceUsd,
    wallet?.subscriptionActive,
    wallet?.subscriptionLabel,
    onStatus,
    onBrainChange,
    tool,
  ]);

  if (selectorOn !== true) return null;

  return (
    <div
      ref={elRef}
      className="af-gpu-browser-mount"
      style={{ minHeight: 360, height: "min(62vh, 640px)" }}
    />
  );
}
